import React, { useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

const inputVariants = cva(
  // text-base below md: iOS Safari zooms focused controls under 16px
  'flex w-full rounded-md border bg-white dark:bg-gray-800 px-3 py-2 text-base text-foreground shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
  {
    variants: {
      variant: {
        default: 'border-neutral-500 dark:border-gray-600 focus-visible:ring-primary-500',
        error: 'border-red-500 dark:border-red-400 focus-visible:ring-red-500',
        success: 'border-green-500 dark:border-green-400 focus-visible:ring-green-500',
      },
      size: {
        default: 'h-10',
        sm: 'h-10 text-base md:h-8 md:text-xs',
        lg: 'h-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof inputVariants> {
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  /** Extra content next to the label (e.g. help tooltip). */
  labelContent?: React.ReactNode;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

function resolveInputVariant(error: string | undefined, variant: InputProps['variant']) {
  return error ? 'error' : variant;
}

function resolveInputRightIcon(type: string | undefined, rightIcon: React.ReactNode) {
  if (type === 'date' && rightIcon === undefined) {
    return <Calendar className='h-4 w-4' />;
  }
  return rightIcon;
}

/** Native time inputs ignore `height` unless WebKit's inner datetime chrome is reset. */
const TIME_INPUT_CLASS =
  'block py-0 leading-none [&::-webkit-calendar-picker-indicator]:my-auto [&::-webkit-datetime-edit]:p-0 [&::-webkit-datetime-edit-fields-wrapper]:p-0 [&::-webkit-datetime-edit-hour-field]:p-0 [&::-webkit-datetime-edit-minute-field]:p-0 [&::-webkit-datetime-edit-second-field]:p-0 [&::-webkit-datetime-edit-ampm-field]:p-0';

function buildInputClassName(args: {
  className?: string;
  variant: InputProps['variant'];
  size: InputProps['size'];
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  type?: string;
}) {
  return cn(
    inputVariants({ variant: args.variant, size: args.size }),
    args.className,
    args.leftIcon && 'pl-10',
    args.rightIcon && 'pr-10',
    args.type === 'date' && !args.rightIcon && 'pr-10',
    args.type === 'date' && 'cursor-pointer shadow-none',
    args.type === 'time' && TIME_INPUT_CLASS,
  );
}

function InputLabel({
  label,
  labelContent,
  inputId,
  required,
}: {
  label?: string;
  labelContent?: React.ReactNode;
  inputId: string;
  required?: boolean;
}) {
  if (!label) return null;
  return (
    <div className='flex items-center gap-2'>
      <label htmlFor={inputId} className='block text-sm font-medium text-foreground'>
        {label}
        {required ? <span className='text-red-500 ml-1'>*</span> : null}
      </label>
      {labelContent}
    </div>
  );
}

function InputMessage({ id, error, helperText }: { id: string; error?: string; helperText?: string }) {
  if (!error && !helperText) return null;
  return (
    <p id={id} className={cn('text-xs mt-1', error ? 'text-red-600 dark:text-red-400' : 'text-muted-strong')}>
      {error || helperText}
    </p>
  );
}

function resolveInputDescribedBy(
  messageId: string,
  error: string | undefined,
  helperText: string | undefined,
  describedByFromProps: string | undefined,
) {
  const parts = [error || helperText ? messageId : undefined, describedByFromProps].filter(
    (part): part is string => !!part,
  );
  return parts.length > 0 ? parts.join(' ') : undefined;
}

function Input({
  className,
  variant,
  size,
  type = 'text',
  label,
  labelContent,
  error: e,
  helperText,
  leftIcon,
  rightIcon,
  id,
  ref,
  ...props
}: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;
  const finalVariant = resolveInputVariant(e, variant);
  const effectiveRightIcon = resolveInputRightIcon(type, rightIcon);
  const { 'aria-describedby': describedByFromProps, ...inputProps } = props;

  return (
    <div className={label ? 'space-y-2' : ''}>
      <InputLabel label={label} labelContent={labelContent} inputId={inputId} required={inputProps.required} />
      <div className='relative'>
        {leftIcon ? (
          <div className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted' aria-hidden>
            {leftIcon}
          </div>
        ) : null}
        <input
          type={type}
          className={buildInputClassName({
            className,
            variant: finalVariant,
            size,
            leftIcon,
            rightIcon: effectiveRightIcon,
            type,
          })}
          ref={ref}
          {...inputProps}
          id={inputId}
          aria-invalid={e ? true : undefined}
          aria-describedby={resolveInputDescribedBy(messageId, e, helperText, describedByFromProps)}
        />
        {effectiveRightIcon ? (
          <div className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted' aria-hidden>
            {effectiveRightIcon}
          </div>
        ) : null}
      </div>
      <InputMessage id={messageId} error={e} helperText={helperText} />
    </div>
  );
}

export { Input };
